import React, { useCallback, useState } from 'react';
import {
  Row, Col, Button, Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Table from 'components/Table';
import TextField from 'components/Form/TextField';
import { DTable } from 'types/table';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import numeral from 'numeral';
import iconDownload from 'assets/img/icons/download.svg';
import { formatCurrencyNoDecimal, formatPercentOneDecimal } from 'utils/formats';
import { updateUserAccount } from 'store/User';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { ImAttachment } from 'react-icons/im';
import { BsCardList } from 'react-icons/bs';
import SyncSolicitudDocFlow from 'screens/Admin/Accounts/components/SyncSolicitudDocFlow';
import SyncSolicitudDocLogsModal from 'screens/Admin/Accounts/components/SyncSolicitudDocLogsModal';

function Account( {
  accounts,
  fetchData,
  isLoading,
  downloadDocument,
  type,
  sincronizarSolicitudDocAction,
  getSyncStatusAction,
  getSyncLogsAction,
}:{
  isLoading?:boolean;
  accounts:DTable;
  fetchData: ( params:any, pageSize:number )=>void ;
  type?: string;
  downloadDocument: ( params:any )=>void ;
  sincronizarSolicitudDocAction: ( id:number, opts?: { silent?: boolean } )=>Promise<any> ;
  getSyncStatusAction: (
    id:number,
    opts?: { runId?: string; silent?: boolean },
  )=>Promise<any> ;
  getSyncLogsAction: ( id:number, params?: any, opts?: { silent?: boolean } )=>Promise<any> ;
} ) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [criteria, setCriteria] = useState( '' );
  const [syncFlowAccountId, setSyncFlowAccountId] = useState<number | null>( null );
  const [logsLoadingAccountId, setLogsLoadingAccountId] = useState<number | null>( null );
  const [showLogsModal, setShowLogsModal] = useState( false );
  const [logsResponse, setLogsResponse] = useState<any>( null );
  const [logsAccountId, setLogsAccountId] = useState<number | null>( null );

  const debounceSearch = useCallback( _.debounce( ( fn ) => fn(), 500 ), [] );

  const handleLogsClick = useCallback( async ( rawId:any ) => {
    const cuentaDeInversionId = Number( rawId );
    if ( !cuentaDeInversionId || logsLoadingAccountId === cuentaDeInversionId ) return;

    setLogsLoadingAccountId( cuentaDeInversionId );
    const logsRes = await getSyncLogsAction(
      cuentaDeInversionId,
      { page: 1, size: 50 },
      { silent: true },
    );
    setLogsAccountId( cuentaDeInversionId );
    setLogsResponse( logsRes );
    setShowLogsModal( true );
    setLogsLoadingAccountId( null );
  }, [getSyncLogsAction, logsLoadingAccountId] );

  let defaultSortBy = [{ id: 'capitalActualInvertido', desc: true }];
  if ( type === 'user' ) {
    defaultSortBy = [{ id: 'capitalActualInvertido', desc: true }];
  }
  if ( type === 'pro' ) {
    defaultSortBy = [{ id: 'capitalActualInvertido', desc: true }];
  }
  return (
    <>
      <Table
        columns={[
          {
            Header: t( 'admin.account.header1' ),
            accessor: 'nameCuentadeInversion',
            headerClassName: 'text--left',
            className: 'text--bold',
            Cell: ( { cell }: CellProps<any> ) => (
              <span
                className="text--bold cursor-pointer"
                onClick={() => {
                  navigate(
                    `/admin/accounts/${cell.row.original.cuentaDeInversionId}/projects/inv`,
                    { state: { nameCuentadeInversion: cell.row.original.nameCuentadeInversion } },
                  );
                }}
              >
                {`${cell.value}`}
              </span>
            ),
          },
          {
            Header: !type ? t( 'admin.account.header2' ) : t( 'admin.account.header21' ),
            className: 'text--right text--lato',
            headerClassName: 'text--right justify-content-end',
            accessor: 'capitalActualInvertido',
            customWidth: 180,
            Cell: ( { cell }: CellProps<any> ) => (
              numeral( cell.value ).format( formatCurrencyNoDecimal ) ),
          },
          {
            accessor: 'beneficiosEstimados',
            className: 'text--right text--lato',
            headerClassName: 'text--right justify-content-end',
            Header: `${t( 'admin.account.header3' )}*`,
            customWidth: 180,
            Cell: ( { cell }: CellProps<any> ) => (
              numeral( cell.value ).format( formatCurrencyNoDecimal ) ),
          },
          {
            Header: `${t( 'admin.account.header4' )}*`,
            accessor: 'roiMedioEstimado',
            headerClassName: 'text--right justify-content-end',
            className: 'text--right text--lato',
            customWidth: 160,
            Cell: ( { cell }: CellProps<any> ) => (
              numeral( cell.value / 100 ).format( formatPercentOneDecimal ) ),
          },
          {
            Header: `${t( 'admin.account.header5' )}*`,
            accessor: 'tirMediaEstimado',
            className: 'text--right text--lato',
            headerClassName: 'text--right',
            customWidth: 145,
            Cell: ( { cell }: CellProps<any> ) => (
              numeral( cell.value / 100 ).format( formatPercentOneDecimal ) ),
          },
          {
            Header: t( 'admin.account.header6' ),
            accessor: 'item5',
            className: 'text--right',
            disableSortBy: true,
            customWidth: 90,
            Cell: ( { cell }:CellProps<any> ) => (
              <div className="d-flex justify-content-center align-items-start">
                <Button
                  className="p-0"
                  variant="link"
                  onClick={() => {
                    dispatch( updateUserAccount( cell.row.original.cuentaDeInversionId ) );
                    window.open( '/', '_blank' );
                  }}
                >
                  <i className="icon-eye-line text-primary icon--xs" />
                </Button>
                <Button
                  className="px-1 py-0"
                  variant="link"
                  title={t( 'admin.account.syncSolicitudDocConfirmTitle' )}
                  aria-label={t( 'admin.account.syncSolicitudDocConfirmTitle' )}
                  onClick={() => {
                    setSyncFlowAccountId( Number( cell.row.original.cuentaDeInversionId ) );
                  }}
                >
                  {syncFlowAccountId === Number( cell.row.original.cuentaDeInversionId )
                    ? <Spinner animation="border" size="sm" />
                    : <ImAttachment className="text--md" />}
                </Button>
                <Button
                  className="p-0"
                  variant="link"
                  title={t( 'admin.account.syncSolicitudDocLogsTitle' )}
                  aria-label={t( 'admin.account.syncSolicitudDocLogsTitle' )}
                  onClick={() => handleLogsClick( cell.row.original.cuentaDeInversionId )}
                >
                  {logsLoadingAccountId === Number( cell.row.original.cuentaDeInversionId )
                    ? <Spinner animation="border" size="sm" />
                    : <BsCardList className="text--md" />}
                </Button>
              </div>
            ),
            headerClassName: 'text--right',
          },
        ]}
        defaultSortBy={defaultSortBy}
        isLoading={isLoading}
        data={accounts.data}
        total={accounts.totalItems}
        pageCount={accounts.pageCount}
        fetchData={fetchData}
        specialText2={t( 'successFee' )}
        header={( filters, setFilters ) => (
          <Row className="align-items-center">
            <Col lg={9} xs={6} sm={8}>
              <Button
                size="sm"
                className="pt-2 pb-2 ps-4 pe-4 text--normal btn--rounded d-flex align-items-center"
                onClick={() => downloadDocument( { download: true } )}
              >
                <div>
                  <img src={iconDownload} alt="" className="icon--img" />
                  {t( 'admin.common.download' )}
                </div>
              </Button>
            </Col>
            <Col lg={3} xs={6} sm={4}>
              <TextField
                value={criteria}
                showErrors={false}
                classNames={{ formGroup: 'input-filters' }}
                appendIcon="icon-search text-primary"
                onChange={( value ) => {
                  setCriteria( value );
                  debounceSearch( () => setFilters( ['nameCuentadeInversion'], value ) );
                }}
                placeholder="search"
              />
            </Col>
          </Row>
        )}
      />
      <SyncSolicitudDocFlow
        accountId={syncFlowAccountId}
        onExited={() => setSyncFlowAccountId( null )}
        sincronizarSolicitudDocAction={sincronizarSolicitudDocAction}
        getSyncStatusAction={getSyncStatusAction}
        getSyncLogsAction={getSyncLogsAction}
      />
      <SyncSolicitudDocLogsModal
        show={showLogsModal}
        selectedAccountId={logsAccountId}
        logsResponse={logsResponse}
        onClose={() => {
          setShowLogsModal( false );
          setLogsResponse( null );
          setLogsAccountId( null );
        }}
      />
    </>
  );
}

export default Account;
